import { yupResolver } from '@hookform/resolvers/yup';
import { checkboxClasses, typographyClasses } from '@mui/material';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { forwardRef, useImperativeHandle } from 'react';
import { Control, FieldErrors, useForm } from 'react-hook-form';

import { PhoneCodeFlagInput } from '~components/FormFields/PhoneCodeFlagInput';
import CheckboxInput from '~components/FormFields/v2/CheckboxInput';
import TextInput from '~components/FormFields/v2/TextInput';
import { Account } from '~hooks/useAccount/models';

import {
  AccountContactDto,
  accountContactSchema,
  setAccountContactSchemaDefaultValues,
} from './accountContactSchema';

export type AccountContactPanelHandler = {
  onSubmit: () => Promise<AccountContactDto | undefined>;
};

interface AccountContactPanelProps {
  account?: Account;
}

const AccountContactPanel = forwardRef<
  AccountContactPanelHandler,
  AccountContactPanelProps
>(function AccountContactPanel({ account }, ref) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<AccountContactDto>({
    resolver: yupResolver(accountContactSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setAccountContactSchemaDefaultValues(account),
  });

  const isBillingContactSame = watch('isBillingContactSame');

  useImperativeHandle(
    ref,
    () => ({
      onSubmit() {
        return new Promise((resolve) => {
          handleSubmit(
            (data) => {
              resolve(data);
            },
            () => {
              resolve(undefined);
            },
          )();
        });
      },
    }),
    [handleSubmit],
  );

  return (
    <>
      <ContactForm control={control} errors={errors} target="primaryContact" />

      <Box sx={{ display: 'inline-block', mt: 2 }}>
        <CheckboxInput
          control={control}
          label={t('account.form.billing_contact_same_as_primary')}
          name="isBillingContactSame"
          sx={{
            [`& .${checkboxClasses.root}`]: { py: 0 },
            [`& .${typographyClasses.root}`]: { fontSize: '13px', fontWeight: 400 },
          }}
        />
      </Box>

      <Collapse in={!isBillingContactSame}>
        <Typography variant="form-section-title" sx={{ my: 2 }}>
          {t('account.form.billing_contact')}
        </Typography>

        <ContactForm control={control} errors={errors} target="billingContact" />
      </Collapse>
    </>
  );
});

interface ContactFormProps {
  control: Control<AccountContactDto>;
  errors: FieldErrors<AccountContactDto>;
  target: 'primaryContact' | 'billingContact';
}

function ContactForm({ control, errors, target }: ContactFormProps) {
  return (
    <Box sx={{ display: 'grid', gap: 2, gridAutoColumns: '1fr', width: '100%' }}>
      <Box sx={{ gridColumn: 'span 2' }}>
        <TextInput
          control={control}
          label={t('form_fields.name')}
          name={`${target}.name`}
        />
      </Box>

      <Box>
        <TextInput
          control={control}
          label={t('form_fields.email')}
          name={`${target}.email`}
        />
      </Box>

      <Box>
        <PhoneCodeFlagInput
          control={control as unknown as Control}
          errors={errors}
          label={t('form_fields.phone')}
          name={`${target}.phone`}
        />
      </Box>
    </Box>
  );
}

export default AccountContactPanel;
